import { CommonModule } from "@angular/common";
import {
	AfterViewInit,
	ChangeDetectionStrategy, ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { FocusModule } from "common/ui/directives/focus/focus.module";
import * as _ from "lodash";
import { TranslateService, WORKLIST_SUFFIX } from '../../../../common/translate';
import { LogManager } from "../../../log/services/log.manager";
import { LogCat } from "../../../log/services/log.manager.categories";
import { FocusDirective } from "../../directives";

/**
 * Search Box Component
 */

@UntilDestroy()
@Component({
	selector: 'cp-search-box-widget',
	templateUrl: './search.box.template.html',
	styleUrls: ['./search.box.style.scss'],
	standalone:true,
	imports: [CommonModule,FormsModule,FocusModule],
	changeDetection: ChangeDetectionStrategy.OnPush
})

export class SearchBoxComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
	@Input() focusOnInit: boolean = false; // whether to focus on init
	@Input() keepFocusOnEnterKeyPress: boolean = false;
	@Input() placeHolders: string[];
	@Input() placeholderTranslationKey: string;
	@Input() showPlaceHolder: boolean = true;
	@Input() placeHolderPlain:string;
	@Input() debounce: number;
	@Input() disabled: boolean;
	@Input() defaultSearchString: string;
	@Input() searchString: string;
	@Input() enableClearButton: boolean = false;
	@Input() customButton: {class: string};
	@Input() readOnly:boolean;
	@Input() inputPattern: string;
	@Input() inputMode: string = "";
	@Input() overrideSearchIconClass:string;

	@Output() search: EventEmitter<string> = new EventEmitter<string>();
	@Output() onClear: EventEmitter<string> = new EventEmitter<string>();
	@Output() onSubmit: EventEmitter<string> = new EventEmitter<string>();
	@Output() customButtonClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output('focus') focusChange = new EventEmitter();
	@Output('blur') blurChange = new EventEmitter();

	@ViewChild(FocusDirective,{static:false}) focusDirective: FocusDirective;
	@ViewChild('searchInput',{static:false}) searchInput: ElementRef;


	public searchTask;
	public lastSearchString: string = "";
	public stopSearch: boolean = false;

	public placeHolder: string;
	public placeHoldersTranslations: any = [];

	public focused:boolean;
	public isModified: boolean = false;

	public showClearButton: boolean = false;

	constructor(private translateService: TranslateService, private detector: ChangeDetectorRef) {
		LogManager.debug(LogCat.UI.SearchBox, "constructor()");
	}


	public ngAfterViewInit(): void {
		this.focusDirective.focusedChanged().pipe(untilDestroyed(this)).subscribe((e) => {
			this.detector.markForCheck();
			this.focused = e.type === "focus";
		});
	}

	public ngOnInit() {
		LogManager.debug(LogCat.UI.SearchBox, "ngOnInit()");
		this.searchString = this.defaultSearchString;

		this.initPlaceHolder();

		if (this.debounce >= 0) {
			this.searchTask = _.debounce(this.onSearch.bind(this), this.debounce);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		//If the new input pattern does not match the current search string, reset it
		if (changes["inputPattern"] && !changes["inputPattern"].isFirstChange() && this.inputPattern && this.searchString) {
				let regEx = new RegExp(this.inputPattern);
				if (!regEx.test(this.searchString)){
					this.clearSearch();
			}
		}
	}

	public ngOnDestroy() {
		LogManager.debug(LogCat.UI.SearchBox, "ngOnDestroy()");

		if (this.searchTask) {
			this.searchTask.cancel();
		}
	}

	/**
	 * Check if to enable/disable search button
	 */
	public isDisabled() {
		LogManager.debug(LogCat.UI.SearchBox, "isDisabled()");
		return this.disabled || (!this.searchString || !_.trim(this.searchString));
	}

	public onSearch($event) {
		LogManager.debug(LogCat.UI.SearchBox, "onSearch()");

		if (!this.stopSearch) {
			if (this.searchTask) {
				this.searchTask.cancel();
			}

			this.search.emit(_.trim(this.searchString));
			this.lastSearchString = this.searchString;
		}

		if ($event) {
			if (this.keepFocusOnEnterKeyPress) {
				setTimeout(() => this.focus(), 0);
			}
			this.onSubmit.emit($event);
		}
	}

	public onCustomButtonCLick($event) {
		this.customButtonClicked.emit(true);
		$event.stopPropagation();
	}

	public onKeydown($event) {
		if (this.inputPattern) {
			let regEx = new RegExp(this.inputPattern);
			if ($event.which !== 8 /*Backspace*/ && !$event.ctrlKey  && !regEx.test($event.key)){
				$event.preventDefault();
			}
		}
	}

	public onMouseDown($event: MouseEvent) {
		if (this.searchTask) {
			this.searchTask();
		}
	}

	public onSearchChange(searchString: string) {

		//Check for prevent copy/paste of invalid pattern text
		if (this.inputPattern && searchString) {
			let regEx = new RegExp(this.inputPattern);
			if (!regEx.test(searchString)){
				searchString = '';
			}
		}

		this.stopSearch = false;
		this.searchString = searchString;

		this.showClearButton = _.trim(searchString) !== '' ? true : false;

		// workaround for bug - https://github.com/angular/angular/issues/17951
		if (this.searchTask && this.lastSearchString !== this.searchString) {
			this.searchTask();
		}
	}

	public onClearHandler() {
		this.searchString = "";
		this.showClearButton = false;
		this.onClear.emit();
	}

	public focus() {
		this.focusDirective.focus();
	}

	public select() {
		this.searchInput.nativeElement.select();
	}

	public blur() {
		this.focusDirective.blur();
	}

	public reset(searchResult?: string) {
		this.stopSearch = true;

		if (this.searchTask) {
			this.searchTask.cancel();
		}

		this.searchString = searchResult ? searchResult : "";

		//If we are reset the control with a given value, we don't want to override the lastSearchString because
		//the following  condition : this.lastSearchString === this.searchString will be true and search task will not be execute
		//This is a fix for #29499
		if (!searchResult) {
			this.lastSearchString = "";
		}

		this.detector.markForCheck();
	}

	public clearSearch() {
		this.onSearchChange('');
	}

	private initPlaceHolder() {
		if (this.showPlaceHolder) {
			if (!this.placeHolderPlain){
				let placeHoldersTranslationsTemp = [];
				if (this.placeHolders) {
					this.placeHoldersTranslations = this.translateService.instant(this.placeHolders);

					this.placeHolders.forEach((k, v) => {
						placeHoldersTranslationsTemp.push(this.placeHoldersTranslations[k]);
					});
				}

				let translationKey = this.placeholderTranslationKey || "Search".concat(WORKLIST_SUFFIX);
				this.placeHolder = <string>this.translateService.instant(translationKey, {a0: placeHoldersTranslationsTemp.join("/")});
			}
			else{
				this.placeHolder = this.placeHolderPlain;
			}

		}
	}
}
